import { useEffect } from "react";
import { Link, useParams, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, AlertCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/components/Layout";
import { BLOG_FR, BLOG_EN, findArticleBySlug, getSlugByIdAndLang } from "@/data/blog";
import { Reveal, RevealHeading } from "@/components/motion-primitives";
import { SectionLabel } from "@/components/ui-blocks";

// Render a paragraph value that can be a plain string OR an array of parts (strings + inline links).
const renderParts = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((part, i) => {
      if (typeof part === "string") return part;
      if (part && part.link) {
        return (
          <a
            key={i}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-accentbrand/50 decoration-2 underline-offset-4 hover:decoration-accentbrand hover:text-accentbrand transition-colors"
          >
            {part.link}
          </a>
        );
      }
      return null;
    });
  }
  return null;
};

const Block = ({ block }) => {
  if (block.type === "h") return <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mt-10 mb-4 tracking-tight">{block.value}</h2>;
  if (block.type === "p") return <p className="text-base text-ink/85 leading-relaxed mb-4">{renderParts(block.value)}</p>;
  if (block.type === "ul") return (
    <ul className="mb-5 space-y-2">
      {block.value.map((li, i) => (
        <li key={i} className="flex gap-3 text-base text-ink/85 leading-relaxed">
          <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-accentbrand" aria-hidden="true" />
          <span>{renderParts(li)}</span>
        </li>
      ))}
    </ul>
  );
  return null;
};

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const b = lang === "en" ? BLOG_EN : BLOG_FR;

  // Resolve article from ANY language's slug (needed after language switch).
  const found = findArticleBySlug(slug);

  // If the URL slug belongs to the OTHER language, redirect to the equivalent slug of the current language.
  useEffect(() => {
    if (found && found.lang !== lang) {
      const targetSlug = getSlugByIdAndLang(found.article.id, lang);
      if (targetSlug && targetSlug !== slug) {
        navigate(`/blog/${targetSlug}`, { replace: true });
      }
    }
  }, [found, lang, slug, navigate]);

  // Set page SEO with the current-language article (once resolved).
  const article = found?.lang === lang
    ? found.article
    : (found ? b.articles.find((a) => a.id === found.article.id) : null);

  const seoTitle = article ? `${article.title} | Gihed Coaching` : b.seoTitle;
  const seoDesc = article?.excerpt || b.seoDesc;
  useSeo(seoTitle, seoDesc, lang);

  // Add hreflang alternates for SEO.
  useEffect(() => {
    if (!article || !found) return undefined;
    const frSlug = getSlugByIdAndLang(article.id, "fr");
    const enSlug = getSlugByIdAndLang(article.id, "en");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const create = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      document.head.appendChild(el);
      return el;
    };
    const links = [];
    if (frSlug) links.push(create("fr", `${origin}/blog/${frSlug}`));
    if (enSlug) links.push(create("en", `${origin}/blog/${enSlug}`));
    if (frSlug) links.push(create("x-default", `${origin}/blog/${frSlug}`));
    return () => links.forEach((l) => l.parentNode && l.parentNode.removeChild(l));
  }, [article, found]);

  if (!article) return <Navigate to="/blog" replace />;

  const idx = b.articles.findIndex((a) => a.id === article.id);
  const next = b.articles[(idx + 1) % b.articles.length];
  const prev = b.articles[(idx - 1 + b.articles.length) % b.articles.length];

  return (
    <div className="min-h-screen" lang={lang}>
      <article className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 pt-32 lg:pt-32 pb-8 lg:pb-12">
        <Reveal>
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground hover:text-accentbrand transition-colors"
          >
            <ArrowLeft size={14} /> {t.blog.backToBlog}
          </Link>
        </Reveal>
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <SectionLabel>{article.category}</SectionLabel>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono-alt uppercase tracking-wider text-muted-foreground">
              <Clock size={12} /> {article.readMin} {t.blog.minRead}
            </span>
          </div>
          <RevealHeading as="h1" text={article.title} className="display-2 text-ink" />
        </div>
        <Reveal delay={0.2}>
          <div className="relative aspect-[16/9] overflow-hidden bg-secondary mt-8">
            <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-center" />
          </div>
        </Reveal>

        {/* BODY */}
        <div className="mt-10">
          {article.blocks.map((block, i) => <Block key={i} block={block} />)}
        </div>

        {/* DISCLAIMER */}
        <aside className="mt-12 border border-border bg-secondary/60 p-5 sm:p-6" data-testid="blog-disclaimer">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-accentbrand shrink-0 mt-0.5" />
            <div>
              <p className="font-mono-alt text-[11px] uppercase tracking-wider text-accentbrand">{t.blog.disclaimerTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.blog.disclaimer}</p>
            </div>
          </div>
        </aside>
      </article>

      {/* PREV / NEXT */}
      <section className="bg-secondary/50 border-t border-border">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-10 lg:py-14 grid sm:grid-cols-2 gap-6">
          <Link to={`/blog/${prev.slug}`} data-testid="blog-prev" className="group block">
            <p className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-2"><ArrowLeft size={12} /> {t.blog.previous}</p>
            <h3 className="mt-2 font-display text-lg font-bold text-ink group-hover:text-accentbrand transition-colors">{prev.title}</h3>
          </Link>
          <Link to={`/blog/${next.slug}`} data-testid="blog-next" className="group block sm:text-right">
            <p className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-2 sm:justify-end">{t.blog.next} <ArrowRight size={12} /></p>
            <h3 className="mt-2 font-display text-lg font-bold text-ink group-hover:text-accentbrand transition-colors">{next.title}</h3>
          </Link>
        </div>
      </section>
    </div>
  );
}
